import {
  theDomWatcher,
  handleNewsletterSubscribed,
  handleContactFormValidation,
  trackNewsletterSignup,
  initProductDescriptions,
} from "@theme/helpers";

const featureLoaders = [
  {
    label: "cart",
    selectors: ["[data-cart-drawer]", "[data-cart-container]", "[data-open-cart]"],
    observe: true,
    loader: () => import("@theme/cart"),
  },
  {
    label: "wishlist",
    // The Saved header icon is a trigger too: the engine must load on
    // pages with no hearts/grids (e.g. the homepage) so it can claim the
    // icon — otherwise it falls through to the raw side-cart behaviour.
    selectors: [
      "[wishlist-button]",
      "[wishlist-grid]",
      "[side-cart-wishlist-grid]",
      '[data-sidecart-tab="wishlist"]',
      "[wishlist-counter]",
    ],
    observe: true,
    loader: () => import("@theme/wishlist"),
  },
  {
    label: "side-cart-tabs",
    selectors: ['.side-cart-tabs[role="tablist"]', "[data-cart-tab]"],
    observe: true,
    loader: () => import("@theme/tabs"),
  },
  {
    label: "toasty",
    selectors: ["body"],
    loader: () => import("@theme/toasty"),
  },
  {
    label: "product-quick-add",
    selectors: ["[quick-add-qf]", "product-form"],
    observe: true,
    loader: () =>
      import("@theme/product-quick-add").then(({ ProductQuickAdd }) => new ProductQuickAdd()),
  },
  {
    label: "quickview",
    selectors: ["product-quick-view", "quickview-modal"],
    loader: () => import("@theme/quickview").then(({ ProductQuickView }) => new ProductQuickView()),
  },
  {
    label: "klaviyo-bis",
    selectors: [".klaviyo-bis-trigger", "[klaviyo-bis-trigger]", "[klaviyo-sold-out-form]"],
    observe: true,
    loader: () => import("@theme/klaviyo-back-in-stock"),
  },
  {
    label: "main-product",
    selectors: ["main-product", "variant-option-picker"],
    observe: true,
    loader: () => import("@theme/main-product"),
  },
  {
    label: "product-gallery-grid",
    selectors: ['[gallery-container][data-gallery-style="grid"]'],
    loader: () => import("@theme/product-gallery"),
  },
  {
    label: "product-card",
    selectors: ["product-card"],
    observe: true,
    loader: () => import("@theme/product-card").then(({ ProductCard }) => new ProductCard()),
  },
  {
    label: "custom-option-dropdown",
    selectors: ["custom-option-dropdown"],
    observe: true,
    loader: () => import("@theme/custom-option-dropdown"),
  },
  {
    label: "predictive-search",
    selectors: ["predictive-search"],
    loader: () => import("@theme/search").then(({ SearchController }) => new SearchController()),
  },
  {
    label: "drawer-menu",
    selectors: [".drawer-menu-trigger", "#open-menu-trigger", ".nav-drawer"],
    loader: () =>
      import("@theme/drawer-menu").then(({ DrawerMenuController }) => new DrawerMenuController()),
  },
  {
    label: "accordion",
    selectors: [".accordion", ".button-accordion"],
    observe: true,
    loader: () =>
      import("@theme/accordion").then(({ AccordionController }) => new AccordionController()),
  },
  {
    label: "product-quantity",
    selectors: ["[quantity-select]"],
    observe: true,
    loader: () =>
      import("@theme/product-quantity").then(({ ProductQuantity }) => new ProductQuantity()),
  },
  {
    label: "header-layout",
    selectors: ["[data-header-layout]"],
    loader: () =>
      import("@theme/header-layout").then(
        ({ HeaderLayoutController }) => new HeaderLayoutController(),
      ),
  },
  {
    label: "header-scroll",
    selectors: [".header-outer-shopify-wrapper"],
    loader: () =>
      import("@theme/header-scroll").then(
        ({ HeaderScrollController }) => new HeaderScrollController(),
      ),
  },
  {
    label: "modal-controller",
    selectors: ["[data-modal-trigger]", "[data-modal]"],
    loader: () =>
      import("@theme/modal-controller").then(({ ModalController }) => {
        if (window.__quickfireModalController) {
          return window.__quickfireModalController;
        }
        const instance = new ModalController();
        window.__quickfireModalController = instance;
        return instance;
      }),
  },
  {
    label: "lightbox",
    selectors: ["[data-lightbox-modal]"],
    observe: true,
    loader: () => import("@theme/lightbox").then(({ bootstrapLightbox }) => bootstrapLightbox()),
  },
  {
    label: "delivery-countdown",
    selectors: ["delivery-countdown"],
    observe: true,
    loader: () => import("@theme/delivery-countdown"),
  },
  {
    label: "size-guide",
    selectors: ['[data-modal-trigger="size-guide"]', '[data-modal="size-guide"]'],
    observe: true,
    loader: () => import("@theme/size-guide").then(({ initSizeGuide }) => initSizeGuide()),
  },
  {
    label: "scroll-slider",
    selectors: ["scroll-slider"],
    observe: true,
    loader: () => import("@theme/scroll-slider"),
  },
];

featureLoaders.forEach(({ selectors, loader, label, observe }) =>
  theDomWatcher(selectors, loader, { label, observe }),
);

theDomWatcher(
  "[data-blaze-slider]",
  () =>
    import("@theme/blaze-slider-controller").then(({ blazeSliderController }) => {
      blazeSliderController.init(document);
      return blazeSliderController;
    }),
  { label: "blaze-slider", observe: true },
);

theDomWatcher("form.newsletter-form", handleNewsletterSubscribed, { label: "newsletter-form" });
theDomWatcher(
  "form.contact--form, form.contact-form, form#contact_form",
  handleContactFormValidation,
  {
    label: "contact-form",
  },
);
theDomWatcher('input[name="contact[email]"]', trackNewsletterSignup, {
  label: "newsletter-signup",
});
theDomWatcher("[read-more-container]", initProductDescriptions, { label: "read-more" });
